import { Button } from "react-aria-components";

import type { ConversationWorkflow } from "./conversationWorkflowSchemas";
import "./conversationWorkflowProgress.css";

interface Props {
  workflow: ConversationWorkflow | undefined;
  stale: boolean;
  onOpenMediaCanvas(jobId: string, resourceId?: string): void;
  onOpenCloudResource(resourceId: string): void;
  onRetryStep(workflow: ConversationWorkflow, step: ConversationWorkflow["steps"][number]): void;
  onReadAgain(): void;
  retryError: string;
  retryingStepId: string;
}

const workflowLabels: Record<ConversationWorkflow["status"], string> = {
  running: "正在处理",
  waiting_approval: "等待审批",
  succeeded: "已完成",
  partial_failed: "部分完成",
  failed: "未完成",
  cancelled: "已取消",
};

const stepLabels: Record<ConversationWorkflow["steps"][number]["status"], string> = {
  queued: "等待开始",
  running: "处理中",
  waiting_approval: "等待审批",
  succeeded: "已完成",
  failed: "失败",
  cancelled: "已取消",
  skipped: "已跳过",
};

const kindLabels: Record<ConversationWorkflow["steps"][number]["kind"], string> = {
  listing: "Listing",
  media: "媒体",
  platform_write: "平台任务",
  fresh: "现采",
  tool: "业务步骤",
};

export function ConversationWorkflowProgress({
  workflow,
  stale,
  onOpenMediaCanvas,
  onOpenCloudResource,
  onRetryStep,
  onReadAgain,
  retryError,
  retryingStepId,
}: Props) {
  if (!workflow) return null;
  const artifactsByStep = new Map<string, ConversationWorkflow["artifacts"]>();
  for (const artifact of workflow.artifacts) {
    const current = artifactsByStep.get(artifact.step_id) ?? [];
    artifactsByStep.set(artifact.step_id, [...current, artifact]);
  }
  const receiptsByStep = new Map(workflow.receipts.map((receipt) => [receipt.step_id, receipt]));
  return (
    <section
      aria-label="本轮任务进度"
      className="conversation-workflow-progress"
      data-workflow-status={workflow.status}
    >
      <header>
        <strong>本轮任务</strong>
        <span role="status">{stale ? "状态暂未同步" : workflowLabels[workflow.status]}</span>
        {stale ? <Button onPress={onReadAgain}>重新读取</Button> : null}
      </header>
      {retryError ? <p className="conversation-workflow-progress__retry-error" role="alert">{retryError}</p> : null}
      <ol>
        {workflow.steps.map((step) => {
          const artifacts = artifactsByStep.get(step.step_id) ?? [];
          const receipt = receiptsByStep.get(step.step_id);
          return (
            <li data-step-status={step.status} key={step.step_id}>
              <div className="conversation-workflow-progress__step-summary">
                <span aria-hidden="true" className="conversation-workflow-progress__marker" />
                <strong>{kindLabels[step.kind]}</strong>
                <span>{step.summary || stepLabels[step.status]}</span>
                <small>{stepLabels[step.status]}</small>
              </div>
              {step.error_code ? (
                <p className="conversation-workflow-progress__error">
                  {step.error_message || "该步骤未完成"}
                  <code>{step.error_code}</code>
                  {step.retryable && step.retry_supported ? (
                    <Button
                      isDisabled={retryingStepId === step.step_id}
                      onPress={() => onRetryStep(workflow, step)}
                    >
                      {retryingStepId === step.step_id ? "正在重试…" : "重试此步骤"}
                    </Button>
                  ) : null}
                  {step.retryable && !step.retry_supported ? (
                    <em>当前类型需重新发起，暂不支持统一重试</em>
                  ) : null}
                </p>
              ) : null}
              {artifacts.length > 0 || receipt?.public_result_id ? (
                <div className="conversation-workflow-progress__artifacts">
                  {artifacts.map((artifact) => {
                    const action = artifact.open_action;
                    return action?.kind === "open_media_canvas" ? (
                      <Button
                        key={artifact.artifact_id}
                        onPress={() => onOpenMediaCanvas(action.job_id, action.resource_id || undefined)}
                      >
                        {artifact.label || "打开媒体成果"}
                      </Button>
                    ) : action?.kind === "open_files" ? (
                      <Button
                        key={artifact.artifact_id}
                        onPress={() => onOpenCloudResource(action.resource_id)}
                      >
                        {artifact.label || "打开文件成果"}
                      </Button>
                    ) : (
                      <span key={artifact.artifact_id}>
                        {artifact.label || artifact.resource_id}
                        <em>暂无安全打开入口</em>
                      </span>
                    );
                  })}
                  {receipt?.public_result_id && artifacts.every((artifact) => artifact.resource_id !== receipt.public_result_id) ? (
                    <span>{receipt.label || "公开回执"}：{receipt.public_result_id}</span>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
